import { Building2, GitBranch, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { t } from "../../i18n";
import type { Forge, Row } from "./SiteOrganizationsPage.utils";

export function SiteOrganizationListContent({
  mode,
  permissions,
  rows,
  forges,
  forgeName,
  setForgeName,
  forgeHost,
  setForgeHost,
  forgeKind,
  setForgeKind,
  forgeToken,
  setForgeToken,
  forgeAppId,
  setForgeAppId,
  forgeClientSecret,
  setForgeClientSecret,
  addForge,
}: Props) {
  return (
    <>
      {mode === "list" && permissions.includes("site:organizations:manage") && (
        <div className="mb-4 flex justify-end">
          <Link className="button-primary" to="/settings/organizations/new">
            {t("Create organization")}
          </Link>
        </div>
      )}
      {mode === "list" && rows && (
        <div className="panel overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-stone-200 text-xs text-stone-500 uppercase dark:border-white/10">
              <tr>
                <th className="p-4">{t("Organization")}</th>
                <th>{t("State")}</th>
                <th>{t("Members")}</th>
                <th>{t("Prompts this month")}</th>
                <th>{t("Subscription")}</th>
                <th>{t("Payment")}</th>
                <th>{t("Security")}</th>
                <th>{t("Edit")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.organization.id}
                  className="border-b border-stone-100 last:border-0 dark:border-white/5"
                >
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Building2 size={16} />
                      <span>
                        <span className="block font-medium">
                          {row.organization.name}
                        </span>
                        <span className="text-xs text-stone-500">
                          {row.organization.slug}
                        </span>
                      </span>
                    </div>
                  </td>
                  <td>{row.organization.state}</td>
                  <td>{row.memberCount}</td>
                  <td>{row.promptCount}</td>
                  <td>{row.subscriptionState ?? "—"}</td>
                  <td>{row.billingStatus ?? "—"}</td>
                  <td>
                    {row.securityStatus === "configured"
                      ? t("configured")
                      : t("attention")}
                  </td>
                  <td className="pr-4">
                    <Link
                      className="button"
                      to={`/settings/organizations/${row.organization.id}/overview`}
                    >
                      <Pencil size={14} /> {t("Edit")}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {mode === "list" &&
        permissions.includes("site:agent-workspaces:manage") && (
          <section className="panel mt-6 p-5">
            <div className="flex items-center gap-2">
              <GitBranch size={18} />
              <h2 className="font-bold">{t("Approved Git forges")}</h2>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-4">
              <input
                className="field"
                placeholder={t("Name")}
                value={forgeName}
                onChange={(event) => setForgeName(event.target.value)}
              />
              <input
                className="field"
                placeholder={t("Host")}
                value={forgeHost}
                onChange={(event) =>
                  setForgeHost(event.target.value.toLowerCase())
                }
              />
              <select
                className="field"
                value={forgeKind}
                onChange={(event) => setForgeKind(event.target.value)}
              >
                <option value="github_app">{t("GitHub App")}</option>
                <option value="gitlab_oauth">{t("GitLab OAuth")}</option>
                <option value="codeberg_oauth">{t("Codeberg OAuth")}</option>
                <option value="forgejo_oauth">{t("Forgejo OAuth")}</option>
                <option value="gitea_oauth">{t("Gitea API token")}</option>
              </select>
              <input
                className="field"
                type="password"
                placeholder={t("Integration token (optional)")}
                value={forgeToken}
                onChange={(event) => setForgeToken(event.target.value)}
              />
              {forgeKind === "gitea_oauth" && (
                <>
                  <input
                    className="field"
                    placeholder={t("Gitea OAuth client ID")}
                    value={forgeAppId}
                    onChange={(event) => setForgeAppId(event.target.value)}
                  />
                  <input
                    className="field"
                    type="password"
                    placeholder={t("Gitea OAuth client secret")}
                    value={forgeClientSecret}
                    onChange={(event) =>
                      setForgeClientSecret(event.target.value)
                    }
                  />
                  <p className="text-xs leading-5 text-stone-500 md:col-span-2">
                    {t("Register this callback URL in Gitea:")}{" "}
                    {forges.find((forge) => forge.kind === "gitea_oauth")
                      ?.oauthCallbackUrl ??
                      `${window.location.origin}/api/git-forges/oauth/callback`}
                  </p>
                </>
              )}
            </div>
            <div className="mt-3 flex justify-end">
              <button
                className="button-primary"
                disabled={!forgeName || !forgeHost}
                onClick={() => void addForge()}
              >
                {t("Approve forge")}
              </button>
            </div>
            <div className="mt-4 divide-y divide-stone-200 dark:divide-white/10">
              {forges.map((forge) => (
                <div
                  key={forge.id}
                  className="flex justify-between py-3 text-sm"
                >
                  <span>
                    <strong>{forge.name}</strong> · {forge.host}
                  </span>
                  <span className="text-xs text-stone-500">
                    {forge.kind} ·{" "}
                    {forge.credentialConfigured
                      ? t("credential configured")
                      : t("no credential")}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
    </>
  );
}

type Props = {
  mode: "list" | "create" | "edit";
  permissions: string[];
  rows: Row[] | undefined;
  forges: Forge[];
  forgeName: string;
  setForgeName: (value: string) => void;
  forgeHost: string;
  setForgeHost: (value: string) => void;
  forgeKind: string;
  setForgeKind: (value: string) => void;
  forgeToken: string;
  setForgeToken: (value: string) => void;
  forgeAppId: string;
  setForgeAppId: (value: string) => void;
  forgeClientSecret: string;
  setForgeClientSecret: (value: string) => void;
  addForge: () => Promise<void>;
};
